import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  inject,
  Input,
  OnChanges,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime, Observable } from 'rxjs';

import { DropdownBase } from './dropdown.base';
import { DropdownSelectComponent } from './dropdown-select.component';

export interface SelectInputHost {
  multi?: boolean;
  value: SelectValue;
  changed: Observable<unknown>;
  dropdown: DropdownBase;
  onItemSelected(i: SelectValue): void;
}

export type SelectValue = object | string | number | null | undefined | unknown[];

@Directive({
  selector: '[dpwSelectValue]',
  exportAs: 'dpwSelectValue',
})
export class SelectItemDirective implements AfterViewInit, OnChanges {
  @Input('dpwSelectValue')
  value: SelectValue;

  @Input()
  dpwSelectClass = 'selected';

  isSelected$ = new BehaviorSubject(false);

  private _destroyRef = inject(DestroyRef);

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(DropdownSelectComponent) public selectComponent: SelectInputHost,
  ) {}

  ngOnChanges(changes: SimpleChanges<SelectItemDirective>): void {
    if (changes.value && !changes.value.firstChange) {
      this.updateSelectedState(this.selectComponent.value);
    }
  }

  ngAfterViewInit(): void {
    this.selectComponent.changed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(v => {
      this.updateSelectedState(v);
    });
    this.selectComponent.dropdown.opened.pipe(debounceTime(0), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.selectComponent.value === this.value) {
        this.elementRef.nativeElement.scrollIntoView({ block: 'center' });
      }
    });
  }

  private updateSelectedState(v: unknown) {
    if (!this.selectComponent.multi) {
      if (this.matchValue(v)) {
        this.renderer.addClass(this.elementRef.nativeElement, this.dpwSelectClass);
        this.isSelected$.next(true);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, this.dpwSelectClass);
        this.isSelected$.next(false);
      }
    } else {
      const foundIndex = ((v as unknown[]) || []).findIndex(i => this.matchValue(i));
      if (foundIndex >= 0) {
        this.renderer.addClass(this.elementRef.nativeElement, this.dpwSelectClass);
        this.isSelected$.next(true);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, this.dpwSelectClass);
        this.isSelected$.next(false);
      }
    }
  }

  private matchValue(v: unknown) {
    const valueWithId = this.value as { id: number };
    if (valueWithId?.id !== undefined) {
      return valueWithId.id === (v as { id: number })?.id;
    }
    return this.value === v;
  }

  @HostListener('click')
  onClick(): void {
    this.selectComponent.onItemSelected(this.value);
  }
}
